/**
 * Facilitators (CC brief 7, Nino approved Design's sketch 7 Oct): the words
 * the merged screen uses. One person = the scheduler's record (status,
 * time zone, sign-up details, rejection reason) joined with the trust row
 * (drills, ratings, no-shows, band) by facilitator_id.
 *
 * Pausing is the trust route's "suspend": the person becomes inactive, so
 * Requests' Live Coach picker leaves them out; "unsuspend" brings them back.
 * Both keep a reason.
 */
import type { FacilitatorActionRow, FacilitatorTrustRow } from '@/lib/api';

export interface SchedulerFacilitator {
  facilitator_id: string;
  display_name: string;
  email: string;
  status: string;
  timezone?: string | null;
  registered_at?: string | null;
  created_at?: string | null;
  rejection_reason?: string | null;
  profile_data?: unknown;
}

export interface Person extends SchedulerFacilitator { trust: FacilitatorTrustRow | null }

export function joinPeople(people: SchedulerFacilitator[], trust: FacilitatorTrustRow[] | undefined): Person[] {
  const byId = new Map((trust ?? []).map((t) => [t.facilitator_id, t]));
  return people.map((p) => ({ ...p, trust: byId.get(p.facilitator_id) ?? null }));
}

export type StatusWord = 'Active' | 'Waiting for approval' | 'Paused' | 'Rejected';
export function statusWord(status: string): StatusWord {
  if (status === 'pending') return 'Waiting for approval';
  if (status === 'inactive' || status === 'suspended') return 'Paused';
  if (status === 'rejected') return 'Rejected';
  return 'Active';
}

export const BAND_WORD: Record<string, string> = { excellent: 'Excellent', good: 'Good', developing: 'Developing', watch: 'Watch', 'at-risk': 'At risk' };
export const needsALook = (p: Person) => statusWord(p.status) === 'Active' && (p.trust?.trust_band === 'watch' || p.trust?.trust_band === 'at-risk');

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "2 no-shows and 1 cancellation in 6 booked drills, rating 3.2": booked = run + cancelled (Design, 10 Oct: say what's counted). */
export function whyText(t: FacilitatorTrustRow | null): string | null {
  if (!t) return null;
  const drills = t.sessions_delivered + t.cancelled_by_facilitator;
  const misses = [t.no_shows > 0 && plural(t.no_shows, 'no-show'), t.cancelled_by_facilitator > 0 && plural(t.cancelled_by_facilitator, 'cancellation')].filter(Boolean).join(' and ');
  const rating = t.avg_facilitator_rating !== null ? `rating ${t.avg_facilitator_rating.toFixed(1)}` : null;
  if (misses) return `${misses} in ${plural(drills, 'booked drill')}${rating ? `, ${rating}` : ''}`;
  if (rating) return `${rating} from ${plural(t.n_ratings, 'rating')}`;
  return t.sessions_delivered > 0 ? `${plural(t.sessions_delivered, 'drill')} run so far` : 'No drills yet';
}

const ZONES: Record<string, string> = {
  'Asia/Jakarta': 'Indonesia · WIB', 'Asia/Pontianak': 'Indonesia · WIB', 'Asia/Makassar': 'Indonesia · WITA', 'Asia/Jayapura': 'Indonesia · WIT',
  'Asia/Singapore': 'Singapore · SGT', 'Asia/Kuala_Lumpur': 'Malaysia · MYT', 'Asia/Manila': 'Philippines · PHT', 'Asia/Bangkok': 'Thailand · ICT',
};
export function placeOf(tz?: string | null): string { return tz ? ZONES[tz] ?? tz.replace(/_/g, ' ') : '—'; }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Mon 6 Oct". */
export function dayDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "22 Sep" (this year), "22 Sep 2025" otherwise. */
export function shortDate(iso?: string | null, now = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Fixed short months: some browsers write "Sept" (Design's sketch says "22 Sep").
  const day = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? day : `${day} ${d.getFullYear()}`;
}

/** "today", "yesterday", "4 days ago". */
export function daysAgo(iso?: string | null, now = Date.now()): string {
  if (!iso) return 'some time ago';
  const days = Math.floor((now - Date.parse(iso)) / 86_400_000);
  if (Number.isNaN(days)) return 'some time ago';
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

const meta = (p: SchedulerFacilitator): Record<string, unknown> => {
  const raw = p.profile_data;
  if (typeof raw === 'string') { try { return JSON.parse(raw) as Record<string, unknown>; } catch { return {}; } }
  return raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
};

/** What they told us at sign-up: "Bandung · Bahasa, English · told us: “…”". */
export function signUpText(p: SchedulerFacilitator): string {
  const m = meta(p);
  const langs = Array.isArray(m.languages) ? (m.languages as string[]).join(', ') : null;
  const bio = typeof m.bio === 'string' && m.bio.trim() ? `told us: “${m.bio.trim()}”` : null;
  const parts = [typeof m.city === 'string' ? m.city : null, langs, bio].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Nothing more from sign-up.';
}

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
export const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/** A log line for notes, outreach, pauses and resumes, newest first. */
export function logLine(a: FacilitatorActionRow): { text: string; who: string; when: string } {
  const p = a.payload ?? {};
  const s = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : '');
  const text = a.action_type === 'note' ? `Note: ${s('body')}`
    : a.action_type === 'outreach' ? `Reached out${s('channel') ? ` on ${s('channel')}` : ''}${s('summary') ? `: ${s('summary')}` : ''}${p.follow_up_needed ? ' · follow-up needed' : ''}`
      : a.action_type === 'suspend' ? `Paused: ${s('reason')}`
        : `Resumed: ${s('reason')}`;
  const who = a.actor_admin_id?.includes('@') ? a.actor_admin_id.split('@')[0] : 'Staff';
  return { text, who: who.charAt(0).toUpperCase() + who.slice(1), when: shortDate(a.created_at) };
}

/** The reason of the latest pause, for the paused line. */
export function pauseReason(actions: FacilitatorActionRow[] | undefined): string | null {
  const last = [...(actions ?? [])].filter((a) => a.action_type === 'suspend').sort((x, y) => y.created_at.localeCompare(x.created_at))[0];
  const r = last?.payload?.reason;
  return typeof r === 'string' && r ? r : null;
}

const SESSION_WORD: Record<string, string> = { completed: 'ran', cancelled: 'cancelled', published: 'booked', tentative: 'booked', in_progress: 'running now' };
export function sessionWord(status: string): string { return SESSION_WORD[status] ?? status.replace(/_/g, ' '); }
